import { PalmettoIcon } from '@palmetto/dsm-components';
import classNames from 'classnames';
import { PROJECT_TYPE_CONFIG } from '../../../../../api/project/constants';
import { PointOfContact, ProjectV2Response } from '../../../../../api/project/types';
import { ModalV3Id, useModalV3Context } from '@helios/frontend-project';
import { useTranslation, Button } from '@helios/frontend-core';
import { useViewport } from '../../../../../hooks/useViewport';


export interface ProjectSummaryProps {
  project: ProjectV2Response;
  bgHighlightBolder?: boolean;
}

const TRACKING_ID = 'project-summary';

export const ProjectSummary = ({ project, bgHighlightBolder }: ProjectSummaryProps) => {
  const { t } = useTranslation();
  const { openModalV3 } = useModalV3Context();
  const { isMobile } = useViewport();
  const stages = PROJECT_TYPE_CONFIG[project.type].stages;
  const totalStages = stages.length;
  const currentStageIndex = stages.indexOf(project.stage);
  const currentStageProgress = currentStageIndex + 1;
  const currentStageTitle = t(`projectProgress.stages.${stages[currentStageIndex]}.title`);
  const activeFeedItemsCount = project.feedItems.length;
  const primaryPointOfContact = project.pointsOfContact?.find((poc) => poc.primary);
  const organizationName = primaryPointOfContact?.parentEntityName;

  const handleOpenPocModal = (pointOfContact: PointOfContact) => {
    openModalV3(ModalV3Id.POC_MODAL, { pointOfContact });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="p-4 flex gap-12">
        {Boolean(PROJECT_TYPE_CONFIG[project.type].iconName) && (
          <PalmettoIcon
            name={PROJECT_TYPE_CONFIG[project.type].iconName}
            size="2xl"
            className="text-icon-primary shrink-0"
            aria-hidden="true"
          />
        )}
        <div className="flex flex-col gap-2">
          <h2 className="heading-100 text-text-primary">
            {t('projectSummary.projectHeading', { name: t(`common.projectType.${project.type}`) })}
          </h2>
          {organizationName && <div className="body-300 text-text-secondary">{organizationName}</div>}
        </div>
      </div>

      <div
        className={classNames('py-8 px-10  rounded-16 flex justify-between gap-12', {
          'bg-canvas-bold': !bgHighlightBolder,
          'bg-canvas-bolder': bgHighlightBolder,
        })}
      >
        <div>
          <div className="body-200 text-text-tertiary">
            {t('projectSummary.stageProgress', { currentStageProgress, totalStages })}
          </div>
          <div className="body-bold-300 text-text-primary">{currentStageTitle}</div>
        </div>
        <div className="flex gap-8 items-center">
          {primaryPointOfContact && (
            <Button
              onClick={() => handleOpenPocModal(primaryPointOfContact)}
              trackingId={`${TRACKING_ID}-poc-detail-button`}
              size="small"
              emphasis="secondary"
              aria-label={t('projectSummary.pocAriaLabel')}
              className="!p-8"
            >
              <PalmettoIcon name="chat" aria-hidden="true" />
            </Button>
          )}
          <Button
            as="a"
            href={`/projects/${project._id}`}
            trackingId={`${TRACKING_ID}-project-link`}
            size="xs"
            layout="hug"
            routeTransitionAnimation={isMobile ? 'push-on-new' : undefined}
          >
            {activeFeedItemsCount > 0
              ? t('projectSummary.cta.reviewTasks', { activeFeedItemsCount })
              : t('projectSummary.cta.reviewProject')}
          </Button>
        </div>
      </div>
    </div>
  );
};
